import type { Patient, Reference } from '@medplum/fhirtypes'
import { ObservationNode as ObservationSchema } from '@seen/core/tiptap/schema'
import { Fragment, type Node as PMNode, Slice } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { ObservationNodeView } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeView.tsx'
import { resilientNodeView } from '#src/components/rich-text-editor/ResilientNodeView.tsx'

interface ObsNodeOptions {
  participant?: Reference<Patient>
  onObservationsChanged?: (payload: {
    added: string[]
    removed: string[]
  }) => void
  onReferencePersisted?: () => void
}

export const ObservationNode = ObservationSchema.extend<ObsNodeOptions>({
  addProseMirrorPlugins() {
    const typeName = this.name
    let lastCutReferenceStrings: Set<string> = new Set()
    const { onObservationsChanged } = this.options
    return [
      new Plugin({
        key: OBSERVATION_PLUGIN_KEY,
        props: {
          // Track which reference strings were CUT
          handleDOMEvents: {
            cut(view) {
              const { state } = view
              const { from, to } = state.selection
              lastCutReferenceStrings =
                collectObservationReferenceStringsInSelection(
                  state.doc,
                  from,
                  to,
                  typeName
                )
              // let browser/prosemirror continue with the actual cut
              return false
            },
          },
          transformPasted: (slice: Slice) => {
            const cutReferenceStrings = lastCutReferenceStrings
            lastCutReferenceStrings = new Set()

            return mapSlice(slice, (node) => {
              if (
                node.type.name === typeName &&
                node.attrs &&
                node.attrs.reference &&
                !cutReferenceStrings.has(node.attrs.reference)
              ) {
                return node.type.create(
                  {
                    ...node.attrs,
                    reference: null,
                  },
                  node.content,
                  node.marks
                )
              }
              return node
            })
          },
        },
        appendTransaction(transactions, oldState, newState) {
          // No doc change → nothing to do
          if (!transactions.some((tr) => tr.docChanged)) {
            return null
          }

          // TODO: multiplayer support via tagging local transactions via tr.setMeta

          const before = collectObservationReferenceStrings(
            oldState.doc,
            typeName
          )
          const beforeSet = new Set(before)
          const after = collectObservationReferenceStrings(
            newState.doc,
            typeName
          )
          const afterSet = new Set(after)

          const removed = before.filter((id) => !afterSet.has(id))
          const added = after.filter((id) => !beforeSet.has(id))

          if ((added.length || removed.length) && onObservationsChanged) {
            onObservationsChanged({ added, removed })
          }

          // No additional transaction to append
          return null
        },
      }),
    ]
  },

  /* The one attribute the shared schema deliberately does not carry: transient
   * per-client focus state, `rendered: false`, so it never reaches stored
   * content and the server has nothing to parse back. Spreads the parent's
   * attributes rather than replacing them — dropping that spread would fork
   * this node's schema from the server's. */
  addAttributes() {
    return {
      ...this.parent?.(),
      autofocus: {
        default: false,
        rendered: false,
      },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(
      resilientNodeView(ObservationNodeView, { inline: true }),
      {
        className: 'group',
      }
    )
  },
})

const OBSERVATION_PLUGIN_KEY = new PluginKey<{
  lastCutReferenceStrings: Set<string>
}>('observationPlugin')

function mapFragment(
  fragment: Fragment,
  f: (node: PMNode) => PMNode
): Fragment {
  const children: PMNode[] = []
  for (let i = 0; i < fragment.childCount; i++) {
    const child = fragment.child(i)
    let mapped = child
    if (child.content.size) {
      mapped = child.copy(mapFragment(child.content, f))
    }
    children.push(f(mapped))
  }
  return Fragment.fromArray(children)
}

function mapSlice(slice: Slice, f: (node: PMNode) => PMNode): Slice {
  return new Slice(
    mapFragment(slice.content, f),
    slice.openStart,
    slice.openEnd
  )
}

function collectObservationReferenceStringsInSelection(
  doc: PMNode,
  from: number,
  to: number,
  nodeTypeName: string
): Set<string> {
  const referenceStrings = new Set<string>()
  doc.nodesBetween(from, to, (node) => {
    if (node.type.name === nodeTypeName && node.attrs.reference) {
      referenceStrings.add(node.attrs.reference)
    }
    return true
  })
  return referenceStrings
}

function collectObservationReferenceStrings(doc: PMNode, typeName: string) {
  const referenceStrings: string[] = []
  doc.descendants((node) => {
    if (node.type.name === typeName && node.attrs.reference) {
      referenceStrings.push(node.attrs.reference)
    }
    return true
  })
  return referenceStrings
}
